import { shouldShowAllFoldersMenuItem } from '../helpers/moveToCommonUtils';
import MoveToFolderType from '../store/schema/MoveToFolderType';
import MoveToMailboxContext from '../store/schema/MoveToMailboxContext';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import { getArchiveMailboxRootFolder } from 'owa-folders';
import { getAllSharedFoldersData } from 'owa-folders';
import { getStore } from '../store/store';

/**
 * Get "view all folders" menu item for all the supported mailboxes
 * @param supportedMoveToMailboxContexts - supported moveTo mailbox contexts
 * @param foldersShown - already shown folders in moveTo mailbox menu items
 * @param styles - the styling namespace from which we can apply appropriate style
 * @param onViewAll - click event handler for the menu items being returned
 * @return - JSX elements which will be added in moveto menu
 */
export default function getViewAllMenuItemsForSupportedContexts(
    supportedMoveToMailboxContexts: MoveToMailboxContext[],
    foldersShown: MoveToFolderType[],
    styles: any,
    onViewAll: (
        ev: React.MouseEvent<EventTarget>,
        moveToMailboxContext: MoveToMailboxContext,
        rootFolderId?: string
    ) => void
): JSX.Element[] {
    const items: JSX.Element[] = [];
    if (shouldShowAllFoldersMenuItem('primary', supportedMoveToMailboxContexts, foldersShown)) {
        const onClickViewAll = (ev: React.MouseEvent<EventTarget>) => {
            onViewAll(ev, 'primary');
        };
        // Add "All folders" menu item
        items.push(
            <div
                className={styles.nonIconMenuItem}
                data-is-focusable={true}
                key={'moveToViewAll'}
                onClick={onClickViewAll}
                title={strings.allFolders}
                aria-label={strings.openAllFoldersText}
                role={'menuitem'}>
                <div className={styles.folderMenuItemContainer}>
                    <span className={styles.folderSuggestion}>{strings.allFolders}</span>
                </div>
            </div>
        );
    }

    if (shouldShowAllFoldersMenuItem('archive', supportedMoveToMailboxContexts, foldersShown)) {
        const onClickViewAllArchive = (ev: React.MouseEvent<EventTarget>) => {
            onViewAll(ev, 'archive');
        };
        // Add "All archive folders" menu item
        items.push(
            <div
                className={styles.nonIconMenuItem}
                data-is-focusable={true}
                key={'moveToViewAllArchive'}
                onClick={onClickViewAllArchive}
                title={getArchiveMailboxRootFolder().DisplayName}>
                <div className={styles.folderMenuItemContainer}>
                    <span className={styles.folderSuggestion}>
                        {getArchiveMailboxRootFolder().DisplayName}
                    </span>
                </div>
            </div>
        );
    }

    if (
        shouldShowAllFoldersMenuItem(
            'sharedFolder' /* mailboxContext */,
            supportedMoveToMailboxContexts,
            foldersShown
        )
    ) {
        const allSharedFoldersData = getAllSharedFoldersData();

        // For each shared folder root push an option in move to control
        allSharedFoldersData.forEach(sharedFolder => {
            const {
                DisplayName: displayName,
                principalSMTPAddress: folderId, // We are assuming principalSMTPAddress as folderId in moveto control
            } = sharedFolder.rootFolder;
            const { moveToSharedFolderRootId, moveToMailboxContext } = getStore();
            // Do not show that shared folder which is already selected to show all of it's child folders.
            // Ex. - If shared folder person 'PersonFoo' is currently selected then moveTo options shouldn't include 'foo' as it's already opened.
            if (
                moveToMailboxContext == 'sharedFolder' &&
                moveToSharedFolderRootId &&
                moveToSharedFolderRootId == folderId
            ) {
                return;
            }

            // OnClick handler for shared folder root
            const onClickViewAllSharedFolder = (ev: React.MouseEvent<EventTarget>) => {
                onViewAll(ev, 'sharedFolder' /* mailboxContext */, folderId /* rootFolderId */);
            };

            items.push(
                <div
                    className={styles.nonIconMenuItem}
                    data-is-focusable={true}
                    key={'moveToViewAllShared_' + folderId}
                    onClick={onClickViewAllSharedFolder}
                    title={displayName}>
                    <div className={styles.folderMenuItemContainer}>
                        <span className={styles.folderSuggestion}>{displayName}</span>
                    </div>
                </div>
            );
        });
    }

    return items;
}
